import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { User, LogOut, Loader2, Award, ShoppingBag, Save } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useData } from "@/context/DataContext";
import { api, money } from "@/lib/api";
import { toast } from "sonner";

const inp = "w-full bg-[#101311] rounded-lg px-3 py-2.5 text-sm border border-emerald-800/50 outline-none focus:border-amber-400";
const STATUS_COLORS = {
  "Completed": "text-emerald-400", "Cancelled": "text-red-400", "Out for Delivery": "text-amber-400",
  "Ready for Pickup": "text-amber-400", "In the Kitchen": "text-amber-300",
};

export default function AccountPage() {
  const { user, loading, login, logout, checkAuth } = useAuth();
  const navigate = useNavigate();

  if (loading) {
    return <div className="min-h-[60vh] grid place-items-center text-amber-400"><Loader2 className="w-8 h-8 animate-spin" /></div>;
  }

  if (!user) {
    return (
      <div className="min-h-[70vh] grid place-items-center px-5">
        <div className="rounded-3xl bg-[#181D1A] border border-emerald-900/60 p-8 sm:p-10 max-w-sm w-full text-center">
          <span className="grid place-items-center w-14 h-14 rounded-full bg-emerald-900/70 text-amber-300 mx-auto"><User className="w-7 h-7" /></span>
          <h1 className="font-display font-black text-2xl uppercase mt-4">Your Account</h1>
          <p className="text-[#FDFBF7]/60 text-sm mt-2">Sign in to save your details, see order history, and track your Sir. King rewards.</p>
          <button
            data-testid="account-google-login"
            onClick={() => login("/account")}
            className="mt-6 w-full py-3.5 rounded-full bg-amber-400 hover:bg-amber-300 text-[#0A2818] font-display font-black uppercase tracking-wide flex items-center justify-center gap-2 transition-colors"
          >
            <i className="fa-brands fa-google" /> Sign in with Google
          </button>
          <button onClick={() => navigate("/")} className="mt-3 text-sm text-[#FDFBF7]/50 hover:text-amber-300">← Back to site</button>
        </div>
      </div>
    );
  }

  return <AccountDashboard user={user} logout={logout} checkAuth={checkAuth} navigate={navigate} />;
}

function AccountDashboard({ user, logout, checkAuth, navigate }) {
  const { settings } = useData();
  const [form, setForm] = useState({ name: user.name || "", phone: user.phone || "", address: user.address || "", zip_code: user.zip_code || "" });
  const [saving, setSaving] = useState(false);
  const [orders, setOrders] = useState([]);
  const [loyalty, setLoyalty] = useState(null);
  const [loadingOrders, setLoadingOrders] = useState(true);

  const loyCfg = settings?.loyalty || {};

  const loadOrders = useCallback(async () => {
    try { const r = await api.get("/my/orders"); setOrders(r.data); } finally { setLoadingOrders(false); }
  }, []);

  const loadLoyalty = useCallback(async () => {
    const digits = (user.phone || "").replace(/\D/g, "");
    if (loyCfg.enabled && digits.length >= 10) {
      try { const r = await api.get(`/loyalty/${digits}`); setLoyalty(r.data); } catch { setLoyalty(null); }
    }
  }, [user.phone, loyCfg.enabled]);

  useEffect(() => { loadOrders(); loadLoyalty(); }, [loadOrders, loadLoyalty]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/my/profile", form);
      await checkAuth();
      toast.success("Profile saved");
      loadLoyalty();
    } catch {
      toast.error("Could not save profile");
    } finally { setSaving(false); }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const threshold = loyalty?.threshold || loyCfg.threshold || 5;
  const progress = loyalty ? loyalty.plates % threshold : 0;

  return (
    <div className="max-w-4xl mx-auto px-5 sm:px-8 py-10">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          {user.picture ? (
            <img src={user.picture} alt={user.name} className="w-12 h-12 rounded-full object-cover ring-2 ring-amber-400/50" />
          ) : (
            <span className="grid place-items-center w-12 h-12 rounded-full bg-emerald-900/70 text-amber-300"><User className="w-6 h-6" /></span>
          )}
          <div>
            <h1 className="font-display font-black text-2xl uppercase leading-none" data-testid="account-name">{user.name || "Welcome"}</h1>
            <p className="text-[#FDFBF7]/60 text-sm">{user.email}</p>
          </div>
        </div>
        <div className="flex gap-2">
          {user.is_admin && <button onClick={() => navigate("/admin")} className="px-4 py-2 rounded-full border border-emerald-700/50 text-amber-300 text-sm font-semibold">Admin</button>}
          <button data-testid="account-logout" onClick={async () => { await logout(); navigate("/"); }} className="flex items-center gap-2 px-4 py-2 rounded-full border border-emerald-700/50 text-amber-300 text-sm font-semibold"><LogOut className="w-4 h-4" /> Sign Out</button>
        </div>
      </div>

      {loyCfg.enabled && (
        <div className="mt-8 rounded-2xl bg-gradient-to-br from-emerald-900/60 to-[#181D1A] border border-amber-500/30 p-6" data-testid="account-loyalty-card">
          <div className="flex items-center gap-2 text-amber-400 font-display font-black uppercase tracking-wide"><Award className="w-5 h-5" /> Sir. King Rewards</div>
          {loyalty && (user.phone || "").replace(/\D/g, "").length >= 10 ? (
            <div className="mt-3">
              {loyalty.rewards_available > 0 && (
                <p className="text-emerald-300 font-bold mb-2" data-testid="account-loyalty-rewards">🎉 {loyalty.rewards_available} free plate{loyalty.rewards_available > 1 ? "s" : ""} ready to redeem at checkout!</p>
              )}
              <p className="text-sm text-[#FDFBF7]/70 mb-2">{progress}/{threshold} plates toward your next free one.</p>
              <div className="flex gap-1.5">
                {Array.from({ length: threshold }).map((_, i) => (
                  <span key={i} className={`h-2.5 flex-1 rounded-full ${i < progress ? "bg-amber-400" : "bg-emerald-900/70"}`} />
                ))}
              </div>
            </div>
          ) : (
            <p className="text-sm text-[#FDFBF7]/60 mt-2">Add your phone number below to start earning — buy {threshold} plates, get 1 free.</p>
          )}
        </div>
      )}

      <div className="mt-8 grid md:grid-cols-2 gap-6">
        <div className="rounded-2xl bg-[#181D1A] border border-emerald-900/60 p-6">
          <h2 className="font-display font-black text-lg uppercase text-amber-400 mb-4">My Details</h2>
          <div className="space-y-3">
            <input data-testid="account-name-input" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Full name" className={inp} />
            <input data-testid="account-phone-input" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="Phone (for rewards & order updates)" className={inp} />
            <input data-testid="account-address-input" value={form.address} onChange={(e) => set("address", e.target.value)} placeholder="Delivery address" className={inp} />
            <input data-testid="account-zip-input" value={form.zip_code} onChange={(e) => set("zip_code", e.target.value)} placeholder="ZIP code" className={inp} />
            <button data-testid="account-save" onClick={save} disabled={saving} className="w-full py-3 rounded-full bg-amber-400 hover:bg-amber-300 disabled:opacity-50 text-[#0A2818] font-display font-black uppercase tracking-wide flex items-center justify-center gap-2">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
            </button>
          </div>
        </div>

        <div className="rounded-2xl bg-[#181D1A] border border-emerald-900/60 p-6">
          <h2 className="font-display font-black text-lg uppercase text-amber-400 mb-4 flex items-center gap-2"><ShoppingBag className="w-5 h-5" /> Order History</h2>
          {loadingOrders ? (
            <div className="py-8 grid place-items-center text-amber-400/70"><Loader2 className="w-6 h-6 animate-spin" /></div>
          ) : orders.length === 0 ? (
            <p className="text-sm text-[#FDFBF7]/50 py-6 text-center">No orders yet. <button onClick={() => navigate("/menu")} className="text-amber-400 font-bold">Start an order →</button></p>
          ) : (
            <div className="space-y-3 max-h-[420px] overflow-y-auto no-scrollbar">
              {orders.map((o) => (
                <button
                  key={o.order_id}
                  data-testid={`account-order-${o.order_number}`}
                  onClick={() => { localStorage.setItem("skk_last_order", o.order_number); navigate("/track"); }}
                  className="w-full text-left rounded-xl bg-[#101311] border border-emerald-800/40 p-3 hover:border-amber-400/50 transition-colors"
                >
                  <div className="flex justify-between items-start gap-2">
                    <span className="font-display font-bold">#{o.order_number}</span>
                    <span className="text-amber-400 font-bold">{money(o.total)}</span>
                  </div>
                  <div className="flex justify-between text-xs mt-1">
                    <span className="text-[#FDFBF7]/50">{(o.created_at || "").slice(0, 10)} · {o.fulfillment_type}</span>
                    <span className={`font-semibold ${STATUS_COLORS[o.status] || "text-[#FDFBF7]/70"}`}>{o.status}</span>
                  </div>
                  <div className="text-xs text-[#FDFBF7]/50 mt-1 truncate">{o.items?.map((i) => `${i.quantity}× ${i.name}`).join(", ")}</div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
